import React, { useContext, useEffect, useRef, useState } from "react";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useImageStyle from '../../core/hooks/useImageStyle';
import useContainerStyle from '../../core/hooks/useContainerStyle';
import { EditContext } from "../../editor/context/EditContext";
import useCropCalculations from "../../core/hooks/useCropCalculations";
import ProgressiveImage from "./ProgressiveImage";
import {
  getPositionStyles,
  calculateHeight,
} from "../utils/mediaUtils";

const IMAGE_FORMAT_STYLES = {
  circle: { borderRadius: "50%" },
  oval: { borderRadius: "50% / 25%" },
  diamond: { clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)" },
  hexagon: {
    clipPath:
      "polygon(25% 6.7%, 75% 6.7%, 100% 50%, 75% 93.3%, 25% 93.3%, 0% 50%)",
  },
  triangle: { clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" },
  star: {
    clipPath:
      "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
  },
  square: { objectFit: "cover", aspectRatio: "1 / 1" },
};

const ROUNDED_BORDER_ONLY_FORMATS = new Set(["rectangle", "square"]);

const stripRoundedBorderClasses = (className = "") =>
  className
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !/^rounded(?:-[^\s]+)?$/.test(token))
    .join(" ");

const ImageItem = ({ item }) => {
  console.log('[IMAGE_DELETE_FLOW] Image component rendering', { 
    itemId: item?.id, 
    hasItem: !!item,
    hasImageUrl: !!item?.image_url,
    imageUrl: item?.image_url 
  });
  
  const imgRef = useRef(null);
  const imageContainerRef = useRef(null);
  const [squareMaskVars, setSquareMaskVars] = useState(null);
  const [circleMaskVars, setCircleMaskVars] = useState(null);
  const { combinedClasses, padding, margin } = useSpacing(item);
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const { styleClasses: imageStyleClasses, inlineStyles: imageInlineStyles } =
    useImageStyle(item);
  const containerClasses = useContainerStyle(item);
  const imageFormat = typeof item?.format === "string" ? item.format : "rectangle";
  const effectiveContainerClasses = ROUNDED_BORDER_ONLY_FORMATS.has(imageFormat)
    ? containerClasses
    : stripRoundedBorderClasses(containerClasses);
  
  // Use modern crop calculations hook
  const { 
    imageStyles, 
    containerStyles, 
    hasCrop, 
    isInteractive,
    hasShape,
    hasRotation
  } = useCropCalculations(item);
  // No extra hooks: use layout sizing so the container can expand with the image

  // Compute dynamic square/circle mask variables without changing layout size
  useEffect(() => {
    if (!imgRef.current) return;

    const element = imgRef.current;

    const updateVars = () => {
      const width = element.clientWidth || 0;
      const height = element.clientHeight || 0;
      if (!width || !height) return;

      // Square mask vars (only applied when format === 'square')
      if (item?.format === 'square') {
        let top = 0, right = 0, bottom = 0, left = 0;
        if (width > height) {
          const gap = (width - height) / 2;
          left = gap;
          right = gap;
        } else if (height > width) {
          const gap = (height - width) / 2;
          top = gap;
          bottom = gap;
        }

        setSquareMaskVars({
          '--square-top': `${top}px`,
          '--square-right': `${right}px`,
          '--square-bottom': `${bottom}px`,
          '--square-left': `${left}px`,
        });
      }

      // Circle mask radius equals half of the shorter side minus 1px to avoid edge clipping
      if (item?.format === 'circle') {
        const shorter = Math.min(width, height);
        const epsilon = 3.5; // prevents clipping at edges across DPRs
        const radius = Math.max(0, (shorter / 2) - epsilon);
        setCircleMaskVars({ '--circle-radius': `${radius}px` });
      }
    };

    updateVars();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateVars());
      resizeObserver.observe(element);
    } else {
      // Fallback: update on window resize
      const onResize = () => updateVars();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    return () => {
      if (resizeObserver) {
        try { resizeObserver.unobserve(element); } catch (e) {}
        try { resizeObserver.disconnect(); } catch (e) {}
      }
    };
  }, [item?.format]);
  
  // Handle null/undefined items gracefully
  if (!item) {
    return null;
  }

  // If no image URL, show a placeholder only in edit mode, hide completely in published mode
  if (!item.image_url || (typeof item.image_url === 'string' && item.image_url.trim() === '')) {
    console.log('[IMAGE_DELETE_FLOW] No image URL found, showing placeholder in edit mode', {
      itemId: item?.id,
      hasImageUrl: !!item.image_url,
      imageUrl: item.image_url,
      isEditMode
    });
    
    // In published mode, hide the component completely if no image
    if (!isEditMode) {
      console.log('[IMAGE_DELETE_FLOW] Published mode - returning null for no image');
      return null;
    }
    
    console.log('[IMAGE_DELETE_FLOW] Edit mode - rendering placeholder');
    // In edit mode, show clickable placeholder
    const finalClasses = `${combinedClasses} ${styleClasses} ${effectiveContainerClasses}`.trim();
    
    // The placeholder must ADAPT to whatever "image space" it is dropped into.
    // In edit mode the component-mode renderers can't yet know an image is empty
    // (items arrive as minimal {id,type,order} refs, so `elementHasRenderableMedia`
    // optimistically reports media), so full/cover modes route this placeholder
    // into a fixed-size, `overflow-hidden` cover cell whose `COVER_FILL` overrides
    // force every descendant `<div>` to `h-full w-full`. That used to stretch the
    // inner content wrapper (pinning the icon/text to the TOP) and a hard
    // `minWidth: 200px` clipped it in narrow cells. Fixes:
    //   - the content wrapper is now a flex column that centers its own content,
    //     so it stays centered even when stretched to the full cell height;
    //   - no fixed `minWidth` — the box (a block-level flex container) simply fills
    //     its cell width and shrinks with it instead of overflowing/clipping.
    // `min-h-[120px]` remains a sensible floor for contained layouts; cover cells
    // override the height via `!h-full`.
    return (
      <div 
        className={`image-empty-placeholder ${finalClasses} min-h-[120px] w-full border-2 border-dashed border-gray-400 bg-gray-100 hover:bg-gray-200 hover:border-gray-500 transition-all duration-200 flex flex-col items-center justify-center text-gray-600 rounded-lg cursor-pointer`}
        style={{ ...inlineStyles, pointerEvents: 'auto' }}
        title="Click to add an image"
      >
        <div className="flex flex-col items-center justify-center text-center p-4 max-w-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-10 h-10 mb-2 text-gray-500 block flex-shrink-0"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
            <circle cx="9" cy="9" r="1.5" />
            <path d="M21 18l-6.5-8-4.5 6-3-4-4 6" />
          </svg>
          <span className="block text-sm font-medium">No Image Selected</span>
          <span className="block text-xs mt-1 opacity-75">Click to add an image</span>
        </div>
      </div>
    );
  }

  // IMPORTANT: padding must NOT live on the <img>. For a replaced element,
  // `border-radius` rounds the *border box* while the bitmap is painted in the
  // *content box* (inset by the padding) — so with big padding the visible image
  // keeps square corners while the rounded corners hide out in the transparent
  // padding area. Keep the rounding (effectiveContainerClasses) + margin on the
  // <img> so the bitmap itself is rounded, and apply the padding to the sizing
  // wrapper below so padding becomes space *around* the rounded image.
  const finalClasses =
    `${margin} ${styleClasses} ${imageStyleClasses} ${effectiveContainerClasses}`.trim();
  
  // Use cropped image URL if available, otherwise fall back to original
  const imageUrl = item.cropped_image_url || item.image_url;

  const shouldApplyContainer = true; // Always use flex container to honor position centering
  const positionStyles = getPositionStyles(item.position);


  const containerStyle = {
    display: "flex",
    justifyContent: positionStyles.justifyContent || 'center',
    alignItems: positionStyles.alignItems || 'center',
    height: "auto",
    width: "100%",
  };

  const getImageStyle = () => {
    // Start with modern crop/transform styles from useCropCalculations
    const imageStyle = { ...imageStyles };

    // Ensure proper object fit to prevent distortion while maintaining size
    // Only set default objectFit if not already set by modern crop system
    if (!imageStyle.objectFit && !hasCrop) {
      imageStyle.objectFit = 'contain';
    }

    // Layout-based sizing: the <img> fills the inner image box (percent applied on the box)
    if (!imageStyles.width) {
      imageStyle.width = '100%';
      imageStyle.maxWidth = '100%';
    }

    if (!imageStyle.height) {
      const finalHeight = calculateHeight(item);
      if (finalHeight != null) {
        imageStyle.height = finalHeight;
      } else {
        imageStyle.height = "auto";
      }
    }

    // Legacy format styles only if not handled by modern system
    if (item.format && IMAGE_FORMAT_STYLES[item.format] && !hasShape) {
      Object.assign(imageStyle, IMAGE_FORMAT_STYLES[item.format]);
    }

    // Legacy rotation only if not handled by modern system
    if (item.rotation && parseFloat(item.rotation) !== 0 && !hasRotation && !isInteractive) {
      imageStyle.transform =
        (imageStyle.transform ? imageStyle.transform + " " : "") +
        `rotate(${item.rotation}deg)`;
    }

    return imageStyle;
  };

  const imageStyle = getImageStyle();
  const mergedInlineStyles = {
    ...imageStyle,
    ...inlineStyles,
    ...imageInlineStyles,
    ...(item?.format === 'square' && squareMaskVars ? squareMaskVars : {}),
    ...(item?.format === 'circle' && circleMaskVars ? circleMaskVars : {}),
  };


  // Debug logging for modern crop system
  if (hasCrop && isInteractive) {
    console.log('[Image] Modern crop active:', { 
      crop_viewport: item.crop_viewport,
      hasCrop, 
      isInteractive,
      hasShape,
      hasRotation,
      imageStyles: imageStyle,
      containerStyles
    });
  }

  // Debug logging for cropped images
  if (item.crop_viewport) {
    console.log('[Image] Crop viewport:', item.crop_viewport, 'hasCrop:', hasCrop);
    console.log('[Image] Using image URL:', imageUrl);
  }

  // Render with cropped image URL (server-side cropping)
  return (
    <div style={{
      ...containerStyle,
      ...containerStyles,
      width: '100%',
      overflow: 'visible' // allow container to expand with image
    }}>
      <div
        ref={imageContainerRef}
        style={{ 
          position: 'relative', 
          display: 'flex', 
          justifyContent: positionStyles.justifyContent || 'center',
          width: '100%' 
        }}
        data-image-id={item.id}
      >
        {(() => {
          const pct = item.max_width_pct_component ?? 50;
          const clampedPct = Math.max(10, Math.min(100, Number(pct)));
          return (
            <div
              className={`image-size-box ${padding}`.trim()}
              style={{
                position: 'relative',
                display: 'inline-block',
                width: `${clampedPct}%`,
                boxSizing: 'border-box',
                // Re-enable interaction on just the image box. In edit mode the
                // EditableElement wrapper is `pointer-events: none` so only the
                // image (its width "plus a bit") is clickable/hoverable instead of
                // the whole row width. `pointer-events` is inherited, so this opts
                // the image back in while the empty side areas stay pass-through.
                ...(isEditMode ? { pointerEvents: 'auto', cursor: 'pointer' } : {}),
                ...(item.max_width != null ? { maxWidth: `${item.max_width}px` } : {}),
              }}>
              <ProgressiveImage
                ref={imgRef}
                src={imageUrl}
                alt={item.alt_text || "Image"}
                style={mergedInlineStyles}
                className={finalClasses}
                wrapperStyle={{ width: "100%" }}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ImageItem;
